import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import TilChoiceCard from '../components/timesheet/TilChoiceCard';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import ModuleAccessDenied from '../components/ui/ModuleAccessDenied';
import {
  Clock, CalendarDays, Lock, CheckCircle, CircleDot, XCircle, CircleDashed,
  AlertCircle, Plane, X, Timer, Undo2,
} from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz, getCurrentPayPeriod } from '../utils/timezone';
import organizationService from '../services/organization';
import myTimesheetService, { type MyPeriodData, type MyPeriodDay } from '../services/myTimesheet';
import leaveService, {
  type CreateLeaveRequest, type LeaveBalance, type LeaveDayInfo, type LeaveDaySegment,
  type LeaveRequest, type LeaveType, negativeBalanceRefusal, canGoNegative,
} from '../services/leave';
import {
  periodShortfall, planCover, windowsForDay, type CoverPart, type ShortfallDay,
} from '../utils/periodShortfall';

// UTC-midnight date shift, matching the pay-period arithmetic in timezone.ts.
function addDaysUTC(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

const WEEKDAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Postgres NUMERIC values can arrive as strings over the API — coerce before
// formatting so a string never trips `.toFixed`.
const hrs = (v: unknown): string => {
  const n = Number(v);
  return (Number.isFinite(n) ? n : 0).toFixed(1);
};
const asNum = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Leave types offered by the quick shortfall pills, with the entitlement key.
const LEAVE_TYPE_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: 'personal', label: 'Personal / Sick' },
  { value: 'annual', label: 'Annual' },
  { value: 'time_in_lieu', label: 'Time in Lieu' },
  { value: 'long_service', label: 'Long Service' },
  { value: 'unpaid', label: 'Unpaid' },
];

const APPROVAL_CHIP: Record<string, { label: string; className: string; icon: typeof CheckCircle }> = {
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700', icon: CircleDot },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle },
  unsubmitted: { label: 'Not submitted', className: 'bg-neutral-100 text-neutral-500', icon: CircleDashed },
};

// What the rest of a cycle's shortfall is covered with, once time in lieu has
// gone. Time in lieu itself is always spent first.
const COVER_TYPE_OPTIONS: { value: LeaveType; label: string }[] = [
  { value: 'annual', label: 'Annual' },
  { value: 'personal', label: 'Personal / Sick' },
  { value: 'long_service', label: 'Long Service' },
  { value: 'unpaid', label: 'Unpaid' },
];

const LEAVE_LABEL: Record<string, string> = {
  time_in_lieu: 'Time in lieu', annual: 'Annual', personal: 'Personal / Sick',
  long_service: 'Long service', unpaid: 'Unpaid', other: 'Other',
};

type LeaveModalState = { kind: 'day'; day: MyPeriodDay };

/**
 * Send leave requests one after another. Once the employee has said yes to
 * going below zero, it holds for the rest of the batch rather than asking once
 * per request. Returns the message to show, and whether every one went.
 */
async function sendLeaveRequests(payloads: CreateLeaveRequest[]): Promise<{ ok: boolean; message: string }> {
  let submitted = 0;
  const notes: string[] = [];
  const partialWarning = () => (submitted > 0
    ? ` ${submitted} of ${payloads.length} requests were already submitted — close this and check the list below.`
    : '');
  try {
    let goNegative = false;
    for (const payload of payloads) {
      let res;
      try {
        res = await leaveService.createRequest(goNegative ? { ...payload, allow_negative_balance: true } : payload);
      } catch (err) {
        const negative = negativeBalanceRefusal(err);
        if (!negative || !canGoNegative(negative) || goNegative) throw err;
        if (!window.confirm(`${negative.message}\n\nRequest it anyway?`)) {
          return { ok: false, message: `${negative.message}${partialWarning()}` };
        }
        goNegative = true;
        res = await leaveService.createRequest({ ...payload, allow_negative_balance: true });
      }
      if (!res.success) return { ok: false, message: `${res.error.message}${partialWarning()}` };
      submitted += 1;
      // The server trims a window around hours already worked; say so.
      const warning = (res as { warning?: string }).warning;
      if (warning) notes.push(warning);
    }
    const what = payloads.length === 1 ? 'Leave request' : `${payloads.length} leave requests`;
    return { ok: true, message: [`${what} submitted for approval.`, ...notes].join(' ') };
  } catch (err: unknown) {
    const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
    // Prefer the server's message (overlap details, balance shortfalls) over
    // an axios-wrapped HTTP status.
    const serverMsg = e?.response?.data?.error?.message;
    return { ok: false, message: `${serverMsg || e?.message || 'Failed to submit leave request'}${partialWarning()}` };
  }
}

/**
 * How much of a date is already spoken for by leave.
 * `fullDay` means a whole-day or multi-day request covers it, so nothing more
 * can be added. Otherwise `hours` is what partial-day requests have claimed so
 * far, and the rest of the day's shortfall is still claimable.
 */
type LeaveCoverage = { hours: number; fullDay: boolean; request: LeaveRequest };

function MyTimesheet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgTz = useOrgTimezone();

  const payFrequency: 'weekly' | 'fortnightly' = user?.pay_frequency === 'weekly' ? 'weekly' : 'fortnightly';

  const [orgSettings, setOrgSettings] = useState<{ payCycleStartDay: number; fortnightlyRefDate: string | null } | null>(null);
  const [tab, setTab] = useState<'current' | 'last'>('current');
  const [data, setData] = useState<MyPeriodData | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleBlocked, setModuleBlocked] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [leaveModal, setLeaveModal] = useState<LeaveModalState | null>(null);
  const [coverOpen, setCoverOpen] = useState(false);
  // A shortfall day has two honest explanations — the hours were worked and
  // never clocked, or they weren't worked at all — so the day's pill opens a
  // chooser instead of going straight to the leave form.
  const [fixDay, setFixDay] = useState<MyPeriodDay | null>(null);
  // Keyed by date: a day can hold more than one declaration row (one per
  // untouched stretch of the roster), and they are withdrawn together.
  const [withdrawingDate, setWithdrawingDate] = useState<string | null>(null);

  // Resolve the current + last pay periods from org settings and the user's frequency.
  const periods = useMemo(() => {
    if (!orgSettings) return null;
    const current = getCurrentPayPeriod(orgSettings.payCycleStartDay, payFrequency, orgTz, orgSettings.fortnightlyRefDate);
    const span = payFrequency === 'fortnightly' ? 14 : 7;
    const last = {
      startDate: addDaysUTC(current.startDate, -span),
      endDate: addDaysUTC(current.endDate, -span),
    };
    return { current, last };
  }, [orgSettings, payFrequency, orgTz]);

  const selectedPeriod = periods ? (tab === 'current' ? periods.current : periods.last) : null;

  // Load org settings + leave balances once.
  useEffect(() => {
    organizationService.getCurrentOrganization().then(res => {
      if (res.success && res.data) {
        const d = res.data as { pay_cycle_start_day?: number; fortnightly_cycle_reference_date?: string | null };
        setOrgSettings({
          payCycleStartDay: d.pay_cycle_start_day ?? 4,
          fortnightlyRefDate: d.fortnightly_cycle_reference_date ? String(d.fortnightly_cycle_reference_date).slice(0, 10) : null,
        });
      } else {
        setOrgSettings({ payCycleStartDay: 4, fortnightlyRefDate: null });
      }
    }).catch(() => setOrgSettings({ payCycleStartDay: 4, fortnightlyRefDate: null }));

    leaveService.getBalances().then(res => {
      if (res.success && res.data) setBalances(res.data);
    }).catch(() => { /* non-critical */ });
  }, []);

  const loadPeriod = useCallback(async () => {
    if (!selectedPeriod) return;
    setLoading(true);
    setError(null);
    try {
      const [periodRes, leaveRes] = await Promise.all([
        myTimesheetService.getMyPeriod(selectedPeriod.startDate, selectedPeriod.endDate),
        leaveService.getRequests(),
      ]);
      if (periodRes.success && periodRes.data) {
        setData(periodRes.data);
      } else {
        setError('Failed to load your timesheet');
      }
      if (leaveRes.success && leaveRes.data) setLeaveRequests(leaveRes.data);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      if (status === 403) setModuleBlocked(true);
      else setError((err as { message?: string })?.message || 'Failed to load your timesheet');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadPeriod();
  }, [selectedPeriod, loadPeriod]);

  // Leave already requested, tallied per date. Tracked in HOURS rather than as
  // a set of covered dates: a day that was short at both ends needs more than
  // one partial-day request, and marking the whole date as covered after the
  // first one would strand the remaining shortfall.
  const leaveByDate = useMemo(() => {
    const map = new Map<string, LeaveCoverage>();
    const bump = (date: string, request: LeaveRequest, hours: number, fullDay: boolean) => {
      const cur = map.get(date);
      if (!cur) map.set(date, { hours, fullDay, request });
      else {
        cur.hours += hours;
        cur.fullDay = cur.fullDay || fullDay;
      }
    };
    for (const lr of leaveRequests) {
      if (lr.status !== 'pending' && lr.status !== 'approved') continue;
      const start = lr.start_date.slice(0, 10);
      const end = lr.end_date.slice(0, 10);
      // Only PENDING hours are subtracted here. Approving leave writes it to
      // the timesheet as an entry, so approved hours are already inside the
      // day's `worked_hours` and have shrunk its shortfall — counting them
      // again would hide the part of the day still left to claim.
      const unbookedHours = lr.status === 'pending' ? asNum(lr.hours) : 0;
      // A partial-day request carries a time window and an hour count; anything
      // else takes the whole day.
      if (lr.start_time && lr.end_time && start === end) {
        bump(start, lr, unbookedHours, false);
        continue;
      }
      let cursor = start;
      // Guard against malformed ranges.
      let guard = 0;
      while (cursor <= end && guard < 400) {
        bump(cursor, lr, 0, true);
        cursor = addDaysUTC(cursor, 1);
        guard++;
      }
    }
    return map;
  }, [leaveRequests]);

  // Shortfall on a day after subtracting leave already requested for it.
  const remainingShortfall = useCallback((day: MyPeriodDay): number => {
    const cov = leaveByDate.get(day.date);
    if (cov?.fullDay) return 0;
    return Math.max(0, day.shortfall_hours - (cov?.hours ?? 0));
  }, [leaveByDate]);

  const availableFor = (type: LeaveType): number | null => {
    const b = balances.find(x => x.leave_type === type);
    return b ? asNum(b.available_hours) : null;
  };

  const handleLeaveSubmitted = (message: string) => {
    setLeaveModal(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000);
    loadPeriod();
  };

  const handleHoursDeclared = (message: string) => {
    setFixDay(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 6000);
    loadPeriod();
  };

  // A day short at both ends is declared as two rows, so withdrawing has to
  // take both. They are withdrawn in sequence and the first failure stops the
  // rest: a half-withdrawn day would leave the employee looking at a shortfall
  // with no way to tell which stretch was still claimed.
  const handleWithdrawDeclared = async (date: string, entryIds: number[]) => {
    if (entryIds.length === 0) return;
    const what = entryIds.length > 1
      ? `Withdraw all ${entryIds.length} stretches of scheduled hours you declared for this day?`
      : 'Withdraw the scheduled hours you declared for this day?';
    if (!window.confirm(what)) return;
    setWithdrawingDate(date);
    setError(null);
    try {
      for (const entryId of entryIds) {
        const res = await myTimesheetService.withdrawMissedClock(entryId);
        if (!res.success) {
          setError((res as { error?: { message?: string } }).error?.message || 'Failed to withdraw the declared hours');
          loadPeriod();
          return;
        }
      }
      setSuccess('Declared hours withdrawn.');
      setTimeout(() => setSuccess(null), 4000);
      loadPeriod();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to withdraw the declared hours');
      loadPeriod();
    } finally {
      setWithdrawingDate(null);
    }
  };

  // The cycle is judged as a whole: extra hours on one day make up a short one
  // (utils/periodShortfall.ts).
  const shortfallInput = useMemo<ShortfallDay[]>(() => (data?.days ?? []).map(d => ({
    date: d.date,
    scheduled_hours: asNum(d.scheduled_hours),
    worked_hours: asNum(d.worked_hours),
    is_public_holiday: d.is_public_holiday,
    locked: d.locked,
    remaining: remainingShortfall(d),
  })), [data, remainingShortfall]);
  const cycle = useMemo(() => periodShortfall(shortfallInput), [shortfallInput]);

  // Time in lieu the cover can spend: the balance less time in lieu already
  // asked for and not yet approved, which the balance does not know about.
  const tilAvailable = useMemo(() => {
    const b = balances.find(x => x.leave_type === 'time_in_lieu');
    const balance = b ? asNum(b.available_hours) : 0;
    const pending = leaveRequests
      .filter(r => r.leave_type === 'time_in_lieu' && r.status === 'pending')
      .reduce((n, r) => n + asNum(r.scheduled_hours ?? r.hours), 0);
    return Math.max(0, balance - pending);
  }, [balances, leaveRequests]);

  // Partial-day windows already asked for, per date, so a new one never overlaps them.
  const takenWindows = useMemo(() => {
    const map = new Map<string, { start_time: string; end_time: string }[]>();
    for (const r of leaveRequests) {
      if (r.status !== 'pending' && r.status !== 'approved') continue;
      if (!r.start_time || !r.end_time) continue;
      const date = r.start_date.slice(0, 10);
      const list = map.get(date) ?? [];
      list.push({ start_time: r.start_time.slice(0, 5), end_time: r.end_time.slice(0, 5) });
      map.set(date, list);
    }
    return map;
  }, [leaveRequests]);

  if (moduleBlocked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="My Timesheet" subtitle="Your pay-cycle timesheet" icon={<Clock className="h-6 w-6 text-white" />} showHistoryButton={false} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ModuleAccessDenied moduleName="Timesheet" />
        </div>
      </div>
    );
  }

  // Rows worth showing: working days, or any day with entries / leave / a public holiday.
  const visibleDays = (data?.days ?? []).filter(d =>
    d.is_working_day || d.entries.length > 0 || d.is_public_holiday || d.is_leave
  );

  const scheduledTotal = data ? asNum(data.totals.scheduled_hours) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="My Timesheet"
        subtitle="Review your current and last pay cycle, and account for any shortfall"
        icon={<Clock className="h-6 w-6 text-white" />}
        showHistoryButton
        onHistoryClick={() => navigate('/timesheet/history')}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex rounded-lg border border-neutral-200 overflow-hidden w-fit mb-6">
          {(['current', 'last'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                tab === t ? 'bg-primary-600 text-white' : 'bg-white text-neutral-600 hover:bg-neutral-50'
              }`}
            >
              {t === 'current' ? 'Current pay cycle' : 'Last pay cycle'}
            </button>
          ))}
        </div>

        {selectedPeriod && (
          <p className="text-sm text-neutral-500 mb-4 flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            {formatLocalDateInTz(selectedPeriod.startDate, orgTz)} — {formatLocalDateInTz(selectedPeriod.endDate, orgTz)}
            <span className="text-neutral-300">·</span>
            <span className="capitalize">{payFrequency}</span>
          </p>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 shrink-0" /> {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" /> {error}
          </div>
        )}

        {data?.locked && (
          <div className="mb-4 p-3 bg-neutral-800 text-white rounded-lg text-sm flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            This pay cycle has been finalised for payroll. It can no longer be changed — contact HR if something looks wrong.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : data ? (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <SummaryTile label="Scheduled" value={data.totals.scheduled_hours} className="text-neutral-800" />
              <SummaryTile label="Worked / covered" value={data.totals.worked_hours} className="text-primary-700" />
              <SummaryTile label="Shortfall" value={cycle.net_hours} className={cycle.net_hours > 0 ? 'text-amber-700' : 'text-neutral-400'} />
            </div>

            {/* The cycle's shortfall, after extra hours on other days */}
            {cycle.short_hours > 0.01 && !data.locked && (cycle.net_hours > 0.01 ? (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3">
                <Plane className="h-5 w-5 text-amber-600 shrink-0" />
                <div className="flex-1 text-sm text-amber-900 space-y-1">
                  <p className="font-semibold">
                    This pay cycle is {hrs(cycle.net_hours)}h short of your {hrs(scheduledTotal)}h.
                  </p>
                  {cycle.extra_hours > 0.01 && (
                    <p>
                      Your short days come to {hrs(cycle.short_hours)}h, and the {hrs(cycle.extra_hours)}h
                      you worked over on other days makes up the rest.
                    </p>
                  )}
                  <p>Worked a day but forgot to clock in? Claim it from its own row below first.</p>
                  <p>
                    Otherwise cover it with leave: time in lieu first
                    {tilAvailable > 0.01 ? ` (you have ${hrs(tilAvailable)}h)` : ' (you have none)'}, then the leave you choose.
                  </p>
                </div>
                <Button onClick={() => setCoverOpen(true)} className="shrink-0">
                  Cover {hrs(cycle.net_hours)}h with leave
                </Button>
              </div>
            ) : (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 text-sm text-green-800">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                <p>
                  Some days are short by {hrs(cycle.short_hours)}h, but you worked {hrs(cycle.extra_hours)}h over
                  on other days, so the cycle has its {hrs(scheduledTotal)}h. Nothing to make up.
                </p>
              </div>
            ))}

            {/* Day list */}
            <Card>
              <CardContent className="p-0">
                {visibleDays.length === 0 ? (
                  <div className="py-12 text-center text-neutral-500">No scheduled days or entries in this pay cycle.</div>
                ) : (
                  <div className="divide-y divide-neutral-100">
                    {visibleDays.map(day => (
                      <DayRow
                        key={day.date}
                        day={day}
                        orgTz={orgTz}
                        coverage={leaveByDate.get(day.date)}
                        remaining={remainingShortfall(day)}
                        onFix={() => setFixDay(day)}
                        onWithdrawDeclared={handleWithdrawDeclared}
                        withdrawingDate={withdrawingDate}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}

        <div className="mt-6">
          <TilChoiceCard
            orgTz={orgTz}
            openAgreement={searchParams.get('til') === 'agree'}
            why={searchParams.get('from') === 'lunch'
              ? 'Working through lunch is only for staff who bank extra hours as time in lieu. Accept this agreement and, once HR approves it, you can record it.'
              : undefined}
          />
        </div>
      </div>

      {fixDay && (
        <FixDayModal
          day={fixDay}
          orgTz={orgTz}
          remaining={remainingShortfall(fixDay)}
          cycleNet={cycle.net_hours}
          onClose={() => setFixDay(null)}
          onRequestLeave={() => {
            const day = fixDay;
            setFixDay(null);
            setLeaveModal({ kind: 'day', day });
          }}
          onDeclared={handleHoursDeclared}
        />
      )}

      {coverOpen && data && (
        <CoverShortfallModal
          days={shortfallInput}
          need={cycle.net_hours}
          tilAvailable={tilAvailable}
          takenWindows={takenWindows}
          orgTz={orgTz}
          availableFor={availableFor}
          onClose={() => setCoverOpen(false)}
          onSubmitted={(message) => { setCoverOpen(false); handleLeaveSubmitted(message); }}
        />
      )}

      {leaveModal && (
        <LeaveRequestModal
          state={leaveModal}
          orgTz={orgTz}
          availableFor={availableFor}
          onClose={() => setLeaveModal(null)}
          onSubmitted={handleLeaveSubmitted}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, className }: { label: string; value: number; className: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-xs text-neutral-500 font-medium mb-1">{label}</p>
        <p className={`text-lg font-bold ${className}`}>{hrs(value)}h</p>
      </CardContent>
    </Card>
  );
}

function DayRow({ day, orgTz, coverage, remaining, onFix, onWithdrawDeclared, withdrawingDate }: {
  day: MyPeriodDay;
  orgTz: string;
  coverage: LeaveCoverage | undefined;
  /** Shortfall left after leave already requested for this date. */
  remaining: number;
  onFix: () => void;
  onWithdrawDeclared: (date: string, entryIds: number[]) => void;
  withdrawingDate: string | null;
}) {
  const chip = APPROVAL_CHIP[day.approval_status] || APPROVAL_CHIP.unsubmitted;
  const ChipIcon = chip.icon;
  // Hours the employee declared off their roster because no clock record
  // exists. A day short before the first clock-in AND after the last one is
  // declared as one row per stretch, so this is a list.
  const declared = day.entries.filter(e => e.missed_clock);
  const declaredHours = declared.reduce((n, e) => n + asNum(e.total_hours), 0);
  // Withdrawable only while it's still the employee's to take back — once the
  // period is submitted or locked, it's the approver's call.
  const withdrawable = declared.length > 0 && !day.locked
    && (day.approval_status === 'unsubmitted' || day.approval_status === 'rejected')
    ? declared
    : null;
  const withdrawing = withdrawingDate === day.date;
  // Still offer the pill when leave only covers part of the day — a day that
  // was short at both ends takes more than one request to cover.
  const showPill = remaining > 0.01 && !day.locked && !day.is_public_holiday;

  return (
    <div className="flex items-center justify-between px-4 py-3 gap-3">
      <div className="min-w-0">
        <p className="font-medium text-neutral-900">
          {WEEKDAY_NAMES[day.weekday]}, {formatLocalDateInTz(day.date, orgTz)}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {day.is_public_holiday && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" title={day.holiday_name || ''}>
              {day.holiday_name || 'Public Holiday'}
            </span>
          )}
          {coverage && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              <Plane className="h-3 w-3" />
              {coverage.request.status === 'approved' ? 'On leave' : 'Leave requested'}
              {!coverage.fullDay && coverage.hours > 0 && ` · ${hrs(coverage.hours)}h`}
            </span>
          )}
          {declared.length > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700"
              title={declared[0].missed_clock_reason
                ? `No clock record — ${declared[0].missed_clock_reason}`
                : 'Scheduled hours claimed — no clock in/out recorded'}
            >
              <Timer className="h-3 w-3" /> {hrs(declaredHours)}h claimed, no clock record
            </span>
          )}
          {day.entries.length > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
              <ChipIcon className="h-3 w-3" /> {chip.label}
            </span>
          )}
          {day.locked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-800 text-white">
              <Lock className="h-3 w-3" /> Locked
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p className="text-sm font-semibold text-neutral-900">{hrs(day.worked_hours)}h</p>
          <p className="text-xs text-neutral-400">of {hrs(day.scheduled_hours)}h</p>
        </div>
        {withdrawable && (
          <button
            onClick={() => onWithdrawDeclared(day.date, withdrawable.map(e => e.id))}
            disabled={withdrawing}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-neutral-500 hover:bg-neutral-100 disabled:opacity-50 transition-colors"
            title="Withdraw the scheduled hours you claimed for this day"
          >
            <Undo2 className="h-3 w-3" /> {withdrawing ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
        {showPill ? (
          <button
            onClick={onFix}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
            title="Claim these hours as worked, or request leave to cover them"
          >
            −{hrs(remaining)}h · Account for {coverage ? 'the rest' : 'this'}
          </button>
        ) : (
          remaining > 0.01 && !day.is_public_holiday && (
            <span className="text-xs text-neutral-400">−{hrs(remaining)}h</span>
          )
        )}
      </div>
    </div>
  );
}

// A shortfall day is either work that was never clocked, or genuine absence.
// Leave used to be the only self-service answer, which meant employees burnt
// entitlement for days they had actually worked. This asks which it was, then
// routes to the matching action: normal hours straight off their roster, or the
// leave form as before.
function FixDayModal({ day, orgTz, remaining, cycleNet, onClose, onRequestLeave, onDeclared }: {
  day: MyPeriodDay;
  orgTz: string;
  /** Shortfall left after any leave already requested for this date. */
  remaining: number;
  /** What the whole cycle is short, after extra hours on other days. */
  cycleNet: number;
  onClose: () => void;
  onRequestLeave: () => void;
  onDeclared: (message: string) => void;
}) {
  const [mode, setMode] = useState<'choose' | 'worked'>('choose');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateLabel = formatLocalDateInTz(day.date, orgTz);
  const scheduledWindow = day.scheduled_start_time && day.scheduled_end_time
    ? `${day.scheduled_start_time}–${day.scheduled_end_time}`
    : null;
  const partlyWorked = day.worked_hours > 0;

  // Declared hours are written into the untouched stretches of the rostered
  // day, so a day whose entries already span its start and finish has nothing
  // to write even while it is short. Offering "I worked" there produced a
  // refusal the employee could do nothing about, so the option states the
  // truth instead. Clamped to `remaining` so the modal never promises more
  // than the day's own pill does.
  const claimable = Math.min(day.claimable_hours ?? remaining, remaining);
  const canClaim = claimable > 0.01;
  // Some of the shortfall is reachable and some is not — say so rather than
  // quietly claiming less than the pill advertised.
  const partiallyClaimable = canClaim && claimable < remaining - 0.01;

  const submitWorked = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await myTimesheetService.declareMissedClock(day.date, reason.trim() || undefined);
      if (res.success && res.data) {
        onDeclared(
          `${hrs(res.data.claimed_hours)}h of normal hours recorded for ${dateLabel}. ` +
          'Your supervisor still has to approve it.'
        );
      } else {
        setError((res as { error?: { message?: string } }).error?.message || 'Failed to record your scheduled hours');
      }
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to record your scheduled hours');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">
            {mode === 'choose' ? 'Account for this day' : 'Claim your scheduled hours'}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-neutral-600">
            <p><span className="font-medium text-neutral-800">{dateLabel}</span></p>
            <p className="mt-1">
              {hrs(remaining)}h short of your {hrs(day.scheduled_hours)}h scheduled
              {scheduledWindow ? ` (${scheduledWindow})` : ''}
              {partlyWorked ? ` · ${hrs(day.worked_hours)}h already recorded` : ''}.
            </p>
          </div>

          {mode === 'choose' ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setMode('worked')}
                disabled={!canClaim}
                className="w-full text-left p-3 rounded-lg border border-neutral-300 enabled:hover:border-primary-500 enabled:hover:bg-primary-50 transition-colors flex items-start gap-3 disabled:bg-neutral-50 disabled:cursor-not-allowed"
              >
                <Clock className={`h-5 w-5 shrink-0 mt-0.5 ${canClaim ? 'text-primary-600' : 'text-neutral-400'}`} />
                <span>
                  <span className={`block text-sm font-medium ${canClaim ? 'text-neutral-900' : 'text-neutral-500'}`}>
                    I worked — I forgot to clock in
                  </span>
                  <span className="block text-xs text-neutral-500 mt-0.5">
                    {canClaim
                      ? `Claim ${hrs(claimable)}h of normal hours from your work schedule. No leave is used.`
                      : day.leave_pending
                        ? 'You have a leave request waiting for approval on this day. Cancel it first if you were at work.'
                        : 'Your entries already run from your scheduled start to your scheduled finish, so there are no unclocked hours to claim. Edit one of those entries, or ask your supervisor to correct the day.'}
                  </span>
                  {partiallyClaimable && (
                    <span className="block text-xs text-amber-700 mt-1">
                      Only {hrs(claimable)}h of the {hrs(remaining)}h falls outside the hours you already
                      have recorded. The rest needs an edit to an existing entry.
                    </span>
                  )}
                </span>
              </button>

              <button
                type="button"
                onClick={onRequestLeave}
                className="w-full text-left p-3 rounded-lg border border-neutral-300 hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-start gap-3"
              >
                <Plane className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <span>
                  <span className="block text-sm font-medium text-neutral-900">I wasn't at work — request leave</span>
                  <span className="block text-xs text-neutral-500 mt-0.5">
                    Cover the shortfall with personal, annual, time in lieu or unpaid leave.
                  </span>
                  {cycleNet < remaining - 0.01 && (
                    <span className="block text-xs text-amber-700 mt-1">
                      {cycleNet > 0.01
                        ? `Your pay cycle is only ${hrs(cycleNet)}h short: extra hours on other days make up the rest. "Cover with leave" at the top takes only what is needed.`
                        : 'Extra hours on other days already make up this day, so your pay cycle needs no leave.'}
                    </span>
                  )}
                </span>
              </button>
            </div>
          ) : (
            <>
              <div className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 text-sm text-neutral-700">
                <p className="font-medium text-neutral-900 mb-1">{hrs(claimable)}h as normal hours</p>
                <p className="text-xs">
                  {partlyWorked
                    ? 'Recorded against every untouched stretch of your scheduled day, around the hours you did clock — one entry per stretch.'
                    : `Recorded against your scheduled ${scheduledWindow || 'hours'}, less your unpaid break.`}
                </p>
                <p className="text-xs mt-1">
                  It goes to your supervisor for approval like any other entry, and is flagged as
                  having no clock in or clock out behind it. Once approved it counts as worked
                  hours — so it is paid as normal time and accrues leave the same way.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Why was there no clock record? (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder="e.g. Phone battery flat — started in the back block"
                />
              </div>
            </>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={mode === 'worked' ? () => setMode('choose') : onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              {mode === 'worked' ? 'Back' : 'Cancel'}
            </button>
            {mode === 'worked' && (
              <Button onClick={submitWorked} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit for approval'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeaveRequestModal({ state, orgTz, availableFor, onClose, onSubmitted }: {
  state: LeaveModalState;
  orgTz: string;
  availableFor: (t: LeaveType) => number | null;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}) {
  const [leaveType, setLeaveType] = useState<LeaveType>('personal');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A day that was partly worked is claimed as one or more explicit windows.
  // The server works out which parts of the scheduled day are still uncovered
  // — a short day can be short before the first clock-in, after the last
  // clock-out, or both — so the form is driven off that list rather than off a
  // single clock time.
  const isPartialDay = state.day.worked_hours > 0;
  const [segments, setSegments] = useState<LeaveDaySegment[]>([]);
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [dayInfo, setDayInfo] = useState<LeaveDayInfo | null>(null);
  const [prepping, setPrepping] = useState(isPartialDay);
  const [prepError, setPrepError] = useState<string | null>(null);

  useEffect(() => {
    if (state.day.worked_hours === 0) return;
    setPrepping(true);
    setPrepError(null);
    leaveService.getDayInfo(state.day.date).then(res => {
      if (!res.success || !res.data) {
        setPrepError('Could not load your schedule for this day, so the leave hours cannot be worked out. Try again shortly.');
        return;
      }
      setDayInfo(res.data);
      // Zero-hour segments are the unpaid break, not a shortfall.
      const usable = res.data.uncovered_segments.filter(s => s.hours > 0);
      setSegments(usable);
      if (usable.length === 0) {
        setPrepError(
          res.data.is_working_day
            ? 'Every scheduled hour on this day is already covered by a timesheet entry, so there is nothing to take as leave.'
            : 'You have no work schedule for this day, so partial-day leave cannot be calculated. Ask your manager to set your work schedule.'
        );
      }
    }).catch(() => {
      setPrepError('Could not load your schedule for this day, so the leave hours cannot be worked out. Try again shortly.');
    }).finally(() => setPrepping(false));
  }, [state]);

  const toggleSegment = (i: number) => {
    setSkipped(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const chosen = segments.filter((_, i) => !skipped.has(i));
  const chosenHours = chosen.reduce((n, s) => n + s.hours, 0);

  // Every request this submit will send. Partial days and multi-run ranges
  // both become several requests — non-overlapping windows on one date, and
  // one whole-day range per contiguous run of untouched days.
  const payloads = useMemo<CreateLeaveRequest[]>(() => {
    const base = { leave_type: leaveType, reason: reason.trim() || undefined };
    if (isPartialDay) {
      return segments
        .filter((_, i) => !skipped.has(i))
        .map(seg => ({
          ...base,
          start_date: state.day.date,
          end_date: state.day.date,
          start_time: seg.start_time,
          end_time: seg.end_time,
        }));
    }
    return [{ ...base, start_date: state.day.date, end_date: state.day.date }];
  }, [state, leaveType, reason, isPartialDay, segments, skipped]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const result = await sendLeaveRequests(payloads);
    setSubmitting(false);
    if (result.ok) onSubmitted(result.message);
    else setError(result.message);
  };

  const available = availableFor(leaveType);
  const rangeLabel = formatLocalDateInTz(state.day.date, orgTz);

  // Never fall back to a whole-day request on a day that was partly worked: it
  // would collide with the existing timesheet entry and become unapprovable,
  // with nothing on screen to explain why.
  const canSubmit = !submitting && !prepping && !prepError && payloads.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Request leave</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-neutral-600">
            <p><span className="font-medium text-neutral-800">{rangeLabel}</span></p>
            {!isPartialDay && (
              <p className="mt-1">Full scheduled day.</p>
            )}
          </div>

          {isPartialDay && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-2">
              {prepping ? (
                <p className="text-sm text-neutral-500">Working out the hours you missed…</p>
              ) : prepError ? (
                <p className="text-sm text-red-600">{prepError}</p>
              ) : (
                <>
                  {dayInfo && (
                    <p className="text-xs text-neutral-500">
                      Scheduled <strong>{dayInfo.scheduled_start_time}–{dayInfo.scheduled_end_time}</strong> ({hrs(dayInfo.scheduled_hours)}h)
                      {' · '}worked <strong>{hrs(dayInfo.worked_hours)}h</strong>
                      {dayInfo.worked_segments.length > 0 && (
                        <> ({dayInfo.worked_segments.map(w => `${w.start_time}–${w.end_time}`).join(', ')})</>
                      )}
                    </p>
                  )}
                  <p className="text-sm font-medium text-neutral-800">
                    {segments.length === 1
                      ? 'Hours to take as leave:'
                      : `You were away in ${segments.length} blocks on this day:`}
                  </p>
                  {segments.map((seg, i) => (
                    <label key={`${seg.start_time}-${seg.end_time}`} className="flex items-center gap-2 text-sm text-neutral-700">
                      <input
                        type="checkbox"
                        checked={!skipped.has(i)}
                        onChange={() => toggleSegment(i)}
                        className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span>
                        <strong>{seg.start_time}–{seg.end_time}</strong>
                        <span className="text-neutral-500"> · {hrs(seg.hours)}h</span>
                      </span>
                    </label>
                  ))}
                  {segments.length > 1 && (
                    <p className="text-xs text-neutral-500">
                      Submitted as {chosen.length} separate request{chosen.length !== 1 ? 's' : ''} so the leave fits around the hours you worked.
                    </p>
                  )}
                  <p className="text-sm text-neutral-800">
                    Total: <strong>{hrs(chosenHours)}h</strong>
                    {dayInfo && chosen.length === segments.length
                      && Math.abs(chosenHours - dayInfo.remaining_scheduled_hours) < 0.01 && (
                      <span className="text-neutral-500"> — covers the day's shortfall exactly.</span>
                    )}
                  </p>
                  {chosen.length === 0 && (
                    <p className="text-sm text-red-600">Select at least one block to request.</p>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Leave type</label>
            <select
              value={leaveType}
              onChange={e => setLeaveType(e.target.value as LeaveType)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {LEAVE_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {available != null && (
              <p className="text-xs text-neutral-500 mt-1">Available balance: {hrs(available)}h</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="e.g. Left early — unwell"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              Cancel
            </button>
            <Button onClick={submit} disabled={!canSubmit}>
              {submitting ? 'Submitting…' : prepping ? 'Loading…' : 'Submit request'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// The cycle's shortfall, covered in one go: time in lieu first, then the leave
// the employee picks, and only up to what the cycle is actually short — extra
// hours on other days have already made up the rest (utils/periodShortfall.ts).
function CoverShortfallModal({ days, need, tilAvailable, takenWindows, orgTz, availableFor, onClose, onSubmitted }: {
  days: ShortfallDay[];
  /** What the cycle is short after extra hours on other days. */
  need: number;
  tilAvailable: number;
  takenWindows: Map<string, { start_time: string; end_time: string }[]>;
  orgTz: string;
  availableFor: (t: LeaveType) => number | null;
  onClose: () => void;
  onSubmitted: (message: string) => void;
}) {
  const [otherType, setOtherType] = useState<LeaveType>('annual');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parts = useMemo(() => planCover(days, need, tilAvailable, otherType), [days, need, tilAvailable, otherType]);
  const byDate = useMemo(() => {
    const map = new Map<string, CoverPart[]>();
    for (const p of parts) map.set(p.date, [...(map.get(p.date) ?? []), p]);
    return [...map.entries()];
  }, [parts]);
  const tilHours = parts.filter(p => p.leave_type === 'time_in_lieu').reduce((n, p) => n + p.hours, 0);
  const otherHours = parts.filter(p => p.leave_type !== 'time_in_lieu').reduce((n, p) => n + p.hours, 0);
  const planned = tilHours + otherHours;
  const leftOver = need - planned;
  const otherAvailable = availableFor(otherType);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    const base = { reason: reason.trim() || undefined };
    const payloads: CreateLeaveRequest[] = [];
    try {
      for (const [date, dayParts] of byDate) {
        // A day nobody has touched takes whole-day leave: no window to place.
        if (dayParts.length === 1 && dayParts[0].whole_day) {
          payloads.push({ ...base, leave_type: dayParts[0].leave_type, start_date: date, end_date: date });
          continue;
        }
        const info = await leaveService.getDayInfo(date);
        if (!info.success || !info.data) {
          setError(`Could not load your schedule for ${formatLocalDateInTz(date, orgTz)}. Try again shortly.`);
          return;
        }
        const windows = windowsForDay(info.data.uncovered_segments, takenWindows.get(date) ?? [], dayParts);
        if (!windows) {
          setError(`${formatLocalDateInTz(date, orgTz)} has no free time in your schedule left to take that leave in. Request that day from its own row.`);
          return;
        }
        for (const w of windows) {
          payloads.push({ ...base, leave_type: w.leave_type, start_date: date, end_date: date, start_time: w.start_time, end_time: w.end_time });
        }
      }
      const result = await sendLeaveRequests(payloads);
      if (result.ok) onSubmitted(result.message);
      else setError(result.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Cover {hrs(need)}h with leave</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-neutral-600">
            Time in lieu is used first
            {tilAvailable > 0.01 ? `, up to the ${hrs(tilAvailable)}h you have` : ', but you have none'}.
            The rest comes from the leave you choose below.
          </p>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Then use</label>
            <select
              value={otherType}
              onChange={e => setOtherType(e.target.value as LeaveType)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {COVER_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {otherAvailable != null && (
              <p className="text-xs text-neutral-500 mt-1">Available balance: {hrs(otherAvailable)}h</p>
            )}
          </div>

          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-2">
            {byDate.length === 0 ? (
              <p className="text-sm text-neutral-600">There is nothing left that can be requested as leave.</p>
            ) : (
              <>
                <ul className="space-y-1 text-sm text-neutral-700">
                  {byDate.map(([date, dayParts]) => (
                    <li key={date}>
                      <span className="font-medium text-neutral-900">{formatLocalDateInTz(date, orgTz)}</span>
                      {': '}
                      {dayParts.map(p => `${hrs(p.hours)}h ${LEAVE_LABEL[p.leave_type] ?? p.leave_type}`).join(', ')}
                      {dayParts.length === 1 && dayParts[0].whole_day && <span className="text-neutral-500"> (whole day)</span>}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-neutral-800 pt-1 border-t border-neutral-200">
                  {tilHours > 0.01 && <>Time in lieu <strong>{hrs(tilHours)}h</strong>{otherHours > 0.01 ? ' · ' : ''}</>}
                  {otherHours > 0.01 && <>{LEAVE_LABEL[otherType]} <strong>{hrs(otherHours)}h</strong></>}
                </p>
              </>
            )}
            {leftOver > 0.01 && (
              <p className="text-xs text-amber-700">
                {hrs(leftOver)}h is too little to request on its own (the least is a quarter of an hour), so it is left out.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="e.g. Left early on Friday"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50"
            >
              Cancel
            </button>
            <Button onClick={submit} disabled={submitting || parts.length === 0}>
              {submitting ? 'Submitting…' : 'Submit for approval'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyTimesheet;
